import React, { useEffect, useRef, useState } from "react";
import { Card, Typography } from "antd";
import { formatSecondsAsMMSS } from "helpers/utils";
import { useLongHover } from "hooks/useLongHover/";
import styled from "styled-components";
import { PauseIcon, PlayIcon } from "@phosphor-icons/react";
import { radius } from "@libertify/ui";

export const VIDEO_PREVIEW_WIDTH = 180;

type VideoPreviewProps = {
  video: File | string;
  width?: number | string;
  height?: number | string;
  minHeight?: number | string;
  fileName?: string;
  actions?: JSX.Element[];
  style?: React.CSSProperties;
  duration?: number;
  onCardClick?: () => void;
  restrictDowndoad?: boolean;
  controls?: boolean;
  hoverAutoplay?: boolean;
  autoplay?: boolean;
  showDescription?: boolean;
};

const PlayButton = styled.div<{ $opacity?: number }>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
  opacity: ${(props) => props.$opacity ?? 0.8};
  transition: opacity 0.2s ease-in-out;
  cursor: pointer;
  color: white;
  background-color: rgba(0, 0, 0, 0.5);
  width: 40px;
  height: 40px;
  border-radius: ${radius.circle};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const VideoPreview: React.FC<VideoPreviewProps> = ({
  video,
  width = VIDEO_PREVIEW_WIDTH,
  height = "auto",
  fileName,
  actions,
  duration,
  onCardClick,
  minHeight = 180,
  restrictDowndoad,
  controls = false,
  hoverAutoplay = true,
  autoplay = false,
  showDescription = false,
  ...props
}) => {
  const [videoSrc, setVideoSrc] = useState<string>("");
  const [videoName, setVideoName] = useState<string>("");
  const videoRef = useRef<HTMLVideoElement>(null);
  const hideControls = !controls;
  const [isPlaying, setIsPlaying] = useState(autoplay);
  const { handleMouseEnter, handleMouseLeave } = useLongHover(200, () => {
    if (!hoverAutoplay) return;
    videoRef.current?.play().catch((error) => {
      console.error("Error playing video", error);
    });
  });

  useEffect(() => {
    if (video instanceof File) {
      const url = URL.createObjectURL(video);
      setVideoSrc(url);
      setVideoName(video.name);
      return () => URL.revokeObjectURL(url);
    } else if (typeof video === "string") {
      setVideoSrc(video);
      const name = video.split("/").pop() || "Video";
      setVideoName(name);
    }
  }, [video]);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.load();
      setIsPlaying(false); // Reset playing state when video changes
    }
  }, [video]);

  const handleMouseEnterWithPlay = () => {
    handleMouseEnter();
  };

  const handleMouseLeaveWithPause = () => {
    handleMouseLeave();
    if (hoverAutoplay) videoRef.current?.pause();
  };

  const handleClick = () => {
    // reset play
    videoRef.current.currentTime = 0;
  };

  const handleVideoEnded = () => {
    setIsPlaying(false);
  };

  const togglePlay = (e: React.MouseEvent) => {
    e.stopPropagation();

    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      video.play();
      setIsPlaying(true);
    } else {
      video.pause();
      setIsPlaying(false);
    }
  };

  if (!videoSrc) {
    return null;
  }

  const { style: propStyle } = props;
  const description = fileName ?? videoName;
  const hasDescription = showDescription && description?.length > 0;
  const coverOnly = !(hasDescription || actions?.length > 0);
  return (
    <Card
      size="small"
      styles={{
        actions: { padding: 10 },
        body: {
          padding: !hasDescription && 0,
          display: hasDescription ? "block" : "none",
        },
      }}
      style={{
        width,
        height,
        minHeight,
      }}
      cover={
        <div style={{ position: "relative" }}>
          <video
            ref={videoRef}
            style={{
              ...propStyle,
              display: "block",
              width: "100%",
              height: "100%",
              objectFit: "cover",
              borderRadius: radius.box,
              borderBottomLeftRadius: coverOnly ? radius.box : 0,
              borderBottomRightRadius: coverOnly ? radius.box : 0,
              cursor: "pointer",
            }}
            preload="metadata"
            controls={false}
            controlsList={restrictDowndoad ? "nodownload" : ""}
            src={videoSrc}
            onMouseEnter={handleMouseEnterWithPlay}
            onMouseLeave={handleMouseLeaveWithPause}
            onClick={handleClick}
            onEnded={handleVideoEnded}
            muted={hideControls || autoplay}
            autoPlay={autoplay}
            loop={autoplay}
          ></video>
          {!hideControls && (
            <PlayButton onClick={(e) => togglePlay(e)}>
              {isPlaying ? (
                <PauseIcon size={32} weight="duotone" />
              ) : (
                <PlayIcon size={32} weight="duotone" />
              )}
            </PlayButton>
          )}
          {duration && duration > 0 && (
            <div
              style={{
                position: "absolute",
                bottom: 6,
                left: 6,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                color: "white",
                padding: "0px 6px",
                borderRadius: 6,
                fontSize: 10,
                lineHeight: "16px",
              }}
            >
              {formatSecondsAsMMSS(duration / 1000)}
            </div>
          )}
        </div>
      }
      actions={actions}
      onClick={onCardClick}
    >
      {hasDescription && (
        <Card.Meta
          description={
            <Typography.Text
              style={{ width: "100%" }}
              ellipsis
              type="secondary"
            >
              {description}
            </Typography.Text>
          }
        />
      )}
    </Card>
  );
};

export default VideoPreview;
